import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ViewChild, AfterViewInit, Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "src/app/shared/services/api.service";
import { ToastrMsgService } from "src/app/shared/services/shared-services/toastr.service";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDeleteDialogComponent } from "src/app/shared/confirm-delete-dialog/confirm-delete-dialog.component";

interface Invoice {
  id: number;
  org_id: number;
  invoice_no: string;
  total: number;
  project_id: number;
  items: any[];
  data: {
    projectId?: number;
    projectName?: string;
    vendorName?: string;
    invoiceDate?: string;
    invoiceAmount?: string;
    paymentTerms?: string;
    workDescription?: string;
    taxDetails?: string;
    invoiceStatus?: string;
    paymentDueDate?: string;
    domain?: string;
    sellerName?: string;
    sellerAddress?: string;
    sellerEmail?: string;
    sellerPhone?: string;
    sellerGSTIN?: string;
  };
  created_at: string;
  updated_at?: string;
}

@Component({
  selector: "app-invoice-list",
  templateUrl: "./invoice-list.component.html",
  styleUrl: "./invoice-list.component.css",
})
export class InvoiceListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    "srNo",
    "invoiceNo",
    "project",
    "vendor",
    "amount",
    "invoiceDate",
    "status",
    "action",
  ];

  dataSource = new MatTableDataSource<Invoice>([]);
  isLoading = false;
  searchKeyword = "";

  // Stats
  totalAmount = 0;
  paidCount = 0;
  pendingCount = 0;
  overdueCount = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  fetchData(): void {
    this.isLoading = true;
    this.apiService.getInvoice().subscribe({
      next: (res: any) => {
        const invoices = res || [];
        this.dataSource.data = invoices;
        this.calculateStats(invoices);
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Error loading invoices:", err);
        this.toastrMsgService.error(
          err.error?.message ||
            err.error?.errorMessage ||
            "Failed to load invoices"
        );
        this.isLoading = false;
      },
    });
  }

  calculateStats(invoices: Invoice[]): void {
    this.totalAmount = invoices.reduce(
      (sum: number, inv: Invoice) => sum + Number(inv.total || 0),
      0
    );
    this.paidCount = invoices.filter(
      (inv: Invoice) => (inv.data?.invoiceStatus || "").toLowerCase() === "paid"
    ).length;
    this.pendingCount = invoices.filter((inv: Invoice) =>
      ["submitted", "pending", "approved"].includes(
        (inv.data?.invoiceStatus || "submitted").toLowerCase()
      )
    ).length;
    this.overdueCount = invoices.filter(
      (inv: Invoice) =>
        (inv.data?.invoiceStatus || "").toLowerCase() === "overdue"
    ).length;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    this.searchKeyword = filterValue;

    this.dataSource.filterPredicate = (data: Invoice, filter: string) => {
      if (!filter) return true;
      const term = filter.toLowerCase();
      const invoiceNo = (data.invoice_no || "").toLowerCase();
      const projectName = (data.data?.projectName || "").toLowerCase();
      const vendorName = (data.data?.vendorName || "").toLowerCase();
      const status = (data.data?.invoiceStatus || "").toLowerCase();
      return (
        invoiceNo.includes(term) ||
        projectName.includes(term) ||
        vendorName.includes(term) ||
        status.includes(term)
      );
    };

    this.dataSource.filter = filterValue;
  }

  filterByStatus(event: Event): void {
    const status = (event.target as HTMLSelectElement).value;
    const value = (status || "").toString().toLowerCase();

    this.dataSource.filterPredicate = (data: Invoice, filter: string) => {
      if (!filter) return true;
      const s = (data.data?.invoiceStatus || "Submitted").toLowerCase();
      return s === filter;
    };

    this.dataSource.filter = value;
  }

  deleteInvoice(item: Invoice): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: "400px",
      data: { itemName: `Invoice ${item.invoice_no}` },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiService.deleteInvoice(item.id).subscribe({
          next: () => {
            this.toastrMsgService.success("Invoice deleted successfully");
            this.fetchData();
          },
          error: (err) => {
            console.error("Error deleting invoice:", err);
            this.toastrMsgService.error(
              err.error?.message || "Failed to delete invoice"
            );
          },
        });
      }
    });
  }

  editInvoice(invoice: Invoice): void {
    this.router.navigate(["/home/edit-invoice", invoice.id]);
  }

  // Download Invoice as PDF
  downloadInvoice(invoice: Invoice): void {
    this.toastrMsgService.info("Generating invoice PDF...");

    const invoiceHTML = this.generateInvoiceHTML(invoice);

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow) {
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 300);
      };
    } else {
      this.toastrMsgService.error(
        "Please allow popups to download the invoice"
      );
    }
  }

  generateInvoiceHTML(invoice: Invoice): string {
    const data = invoice.data || {};
    const issuerName = this.escapeHtml(data.sellerName || "Organization");
    const issuerAddressHtml = this.formatIssuerAddressHtml(data.sellerAddress);
    const issuerContactHtml = this.formatIssuerContactHtml(data);
    const issuerGstHtml = data.sellerGSTIN
      ? `<p>GSTIN: ${this.escapeHtml(data.sellerGSTIN)}</p>`
      : "";
    const invoiceDate = this.formatDateForPdf(
      data.invoiceDate || invoice.created_at
    );
    const dueDate = data.paymentDueDate
      ? this.formatDateForPdf(data.paymentDueDate)
      : "N/A";
    const status = data.invoiceStatus || "Submitted";
    const statusColor = this.getStatusColorForPdf(status);

    // Calculate tax (assuming 18% GST if not specified)
    const subtotal = Number(invoice.total) || 0;
    const taxRate = 0.18;
    const taxAmount = subtotal * taxRate;
    const grandTotal = subtotal + taxAmount;

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice ${invoice.invoice_no}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: #fff;
      color: #1f2937;
      padding: 40px;
      line-height: 1.6;
    }
    .invoice-container {
      max-width: 800px;
      margin: 0 auto;
      background: #fff;
    }
    
    /* Header */
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 30px;
      border-bottom: 3px solid #3b82f6;
      margin-bottom: 30px;
    }
    .company-info h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1e40af;
      margin-bottom: 5px;
    }
    .company-info p {
      color: #6b7280;
      font-size: 14px;
    }
    .invoice-title {
      text-align: right;
    }
    .invoice-title h2 {
      font-size: 36px;
      font-weight: 700;
      color: #1e40af;
      letter-spacing: 2px;
    }
    .invoice-number {
      font-size: 16px;
      color: #374151;
      font-weight: 600;
      margin-top: 5px;
    }
    .invoice-date {
      font-size: 14px;
      color: #6b7280;
      margin-top: 3px;
    }
    .status-badge {
      display: inline-block;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      margin-top: 10px;
      background: ${statusColor.bg};
      color: ${statusColor.text};
    }
    
    /* Billing Section */
    .billing-section {
      display: flex;
      justify-content: space-between;
      margin-bottom: 40px;
    }
    .billing-box {
      flex: 1;
    }
    .billing-box h3 {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #6b7280;
      margin-bottom: 10px;
    }
    .billing-box p {
      font-size: 15px;
      color: #1f2937;
      margin-bottom: 5px;
    }
    .billing-box .highlight {
      font-weight: 600;
      font-size: 16px;
    }
    
    /* Items Table */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    .items-table th {
      background: #f1f5f9;
      padding: 14px 16px;
      text-align: left;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #475569;
      border-bottom: 2px solid #e2e8f0;
    }
    .items-table td {
      padding: 16px;
      border-bottom: 1px solid #e2e8f0;
      font-size: 14px;
      color: #374151;
    }
    .items-table .text-right {
      text-align: right;
    }
    .items-table .amount {
      font-weight: 600;
      color: #1f2937;
    }
    
    /* Totals */
    .totals-section {
      margin-left: auto;
      width: 320px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid #e2e8f0;
    }
    .totals-row .label {
      color: #6b7280;
      font-size: 14px;
    }
    .totals-row .value {
      font-weight: 600;
      color: #374151;
      font-size: 14px;
    }
    .totals-row.grand-total {
      border-bottom: none;
      border-top: 2px solid #1e40af;
      padding-top: 15px;
      margin-top: 5px;
    }
    .totals-row.grand-total .label {
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    }
    .totals-row.grand-total .value {
      font-size: 20px;
      color: #1e40af;
    }
    
    /* Payment Info */
    .payment-info {
      background: #f8fafc;
      padding: 20px;
      border-radius: 8px;
      margin-top: 30px;
    }
    .payment-info h4 {
      font-size: 14px;
      color: #374151;
      margin-bottom: 10px;
    }
    .payment-info p {
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 5px;
    }
    
    /* Footer */
    .invoice-footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e2e8f0;
      text-align: center;
    }
    .invoice-footer p {
      font-size: 12px;
      color: #9ca3af;
    }
    .invoice-footer .thank-you {
      font-size: 14px;
      color: #374151;
      margin-bottom: 5px;
    }
    
    @media print {
      body { padding: 20px; }
      .invoice-container { max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- Header -->
    <div class="invoice-header">
      <div class="company-info">
        <h2>${issuerName}</h2>
        ${issuerAddressHtml}
        ${issuerContactHtml}
        ${issuerGstHtml}
      </div>
       <div class="invoice-title">
        <h2>INVOICE</h2>
        <p class="invoice-number">${invoice.invoice_no}</p>
        <p class="invoice-date">Date: ${invoiceDate}</p>
        <span class="status-badge">${status.toUpperCase()}</span>
      </div>
    </div>
    
    <!-- Billing Section -->
    <div class="billing-section">
      <div class="billing-box">
        <h3>Bill To</h3>
        <p class="highlight">${data.vendorName || "N/A"}</p>
        <p>Project: ${data.projectName || `Project #${invoice.project_id}`}</p>
        <p>Domain: ${data.domain || "N/A"}</p>
      </div>
      <div class="billing-box" style="text-align: right;">
        <h3>Payment Details</h3>
        <p><strong>Due Date:</strong> ${dueDate}</p>
        <p><strong>Payment Terms:</strong> ${data.paymentTerms || "Net 30"}</p>
        <p><strong>Tax:</strong> ${data.taxDetails || "GST @ 18%"}</p>
      </div>
    </div>
    
    <!-- Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 50px;">#</th>
          <th>Description</th>
          <th style="width: 120px;">Domain</th>
          <th style="width: 120px;" class="text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>${data.workDescription || "Invoice Item"}</td>
          <td>${data.domain || "N/A"}</td>
          <td class="text-right amount">${this.formatCurrency(subtotal)}</td>
        </tr>
      </tbody>
    </table>
    
    <!-- Totals -->
    <div class="totals-section">
      <div class="totals-row">
        <span class="label">Subtotal</span>
        <span class="value">${this.formatCurrency(subtotal)}</span>
      </div>
      <div class="totals-row">
        <span class="label">Tax (GST 18%)</span>
        <span class="value">${this.formatCurrency(taxAmount)}</span>
      </div>
      <div class="totals-row grand-total">
        <span class="label">Grand Total</span>
        <span class="value">${this.formatCurrency(grandTotal)}</span>
      </div>
    </div>
    
    <!-- Payment Info -->
    <div class="payment-info">
      <h4>Payment Information</h4>
      <p><strong>Payment Terms:</strong> ${data.paymentTerms || "Net 30"}</p>
      <p><strong>Due Date:</strong> ${dueDate}</p>
      <p>Please ensure payment is made by the due date to avoid any late fees.</p>
    </div>
    
    <!-- Footer -->
    <div class="invoice-footer">
      <p class="thank-you">Thank you for your business!</p>
      <p>This is a computer-generated invoice and does not require a signature.</p>
      <p>Generated on ${new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}</p>
    </div>
  </div>
</body>
</html>`;
  }

  private escapeHtml(text: string | undefined | null): string {
    if (text == null || text === "") return "";
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private formatIssuerAddressHtml(address: string | undefined): string {
    if (!address || !String(address).trim()) {
      return "<p>—</p>";
    }
    const esc = this.escapeHtml(String(address).trim());
    return `<p>${esc.replace(/\n/g, "<br>")}</p>`;
  }

  private formatIssuerContactHtml(data: Invoice["data"]): string {
    const parts: string[] = [];
    if (data.sellerEmail) {
      parts.push(this.escapeHtml(data.sellerEmail));
    }
    if (data.sellerPhone) {
      parts.push(`Mobile: ${this.escapeHtml(data.sellerPhone)}`);
    }
    if (!parts.length) {
      return "";
    }
    return `<p>${parts.join(" | ")}</p>`;
  }

  formatDateForPdf(dateString: string): string {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  getStatusColorForPdf(status: string): { bg: string; text: string } {
    const s = status.toLowerCase();
    switch (s) {
      case "paid":
        return { bg: "#dcfce7", text: "#166534" };
      case "approved":
        return { bg: "#dbeafe", text: "#1e40af" };
      case "submitted":
      case "pending":
        return { bg: "#fef3c7", text: "#92400e" };
      case "overdue":
        return { bg: "#fee2e2", text: "#991b1b" };
      case "cancelled":
        return { bg: "#f3f4f6", text: "#4b5563" };
      default:
        return { bg: "#f3f4f6", text: "#4b5563" };
    }
  }

  getStatusClass(invoice: Invoice): string {
    const status = (invoice.data?.invoiceStatus || "submitted").toLowerCase();
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "approved":
        return "bg-blue-100 text-blue-800";
      case "submitted":
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }

  formatCurrency(amount: number | string): string {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num || 0);
  }
}
